// Calendar days as `YYYY-MM-DD` strings (the platform's `isDay`, `shiftDay`, `weekStart`).
// Business days skip Saturday, Sunday and the holidays a caller passes (`Holidays`, from
// `holidays.ts`: the 6 US holidays and Jewish Yom Tov days) — this file is the one place that counts
// them, worked at noon UTC so no zone shifts a day. No global state: every caller passes its set.

import { isDay, shiftDay, weekStart } from "@axny/platform-contracts";

export { shiftDay as addDays, weekStart as weekMonday } from "@axny/platform-contracts";

/** A store or wire value as a day: `2026-10-15`, `2026-10-15T00:00:00.000Z` → `2026-10-15`. */
export function asDay(v: unknown): string | null {
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v !== "string" || v.length < 10) return null;
  const d = v.slice(0, 10);
  return isDay(d) ? d : null;
}

const toDate = (day: string) => new Date(`${day}T12:00:00Z`);
const fromDate = (d: Date) => d.toISOString().slice(0, 10);
const isWeekend = (d: Date) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

/** The days besides weekends that are not business days: `YYYY-MM-DD` → the holiday's name. */
export type Holidays = ReadonlyMap<string, string>;
/** Weekends only: what business days were before holidays (and `ROUTING_HOLIDAYS=none`). */
export const NO_HOLIDAYS: Holidays = new Map();

const isOff = (d: Date, holidays: Holidays) => isWeekend(d) || holidays.has(fromDate(d));

/** `day` moved by `n` business days (negative: back), past weekends and `holidays`. */
export function addBusinessDays(day: string, n: number, holidays: Holidays): string {
  const d = toDate(day);
  const step = n < 0 ? -1 : 1;
  for (let left = Math.abs(n); left > 0;) {
    d.setUTCDate(d.getUTCDate() + step);
    if (!isOff(d, holidays)) left--;
  }
  return fromDate(d);
}

/** Signed business days from `from` to `to`: 0 on the same day, negative when `to` is earlier. */
export function businessDaysBetween(from: string, to: string, holidays: Holidays): number {
  if (from === to) return 0;
  const step = from < to ? 1 : -1;
  const d = toDate(from);
  let n = 0;
  while (fromDate(d) !== to) {
    d.setUTCDate(d.getUTCDate() + step);
    if (!isOff(d, holidays)) n += step;
  }
  return n;
}

/**
 * The holidays a count stepped over: those on a weekday strictly between `a` and `b` (either
 * order). One on a weekend changed nothing, so it is not named.
 */
export function holidaysBetween(a: string, b: string, holidays: Holidays): { day: string; title: string }[] {
  const [from, to] = a < b ? [a, b] : [b, a];
  const out: { day: string; title: string }[] = [];
  for (const [day, title] of holidays) {
    if (day > from && day < to && !isWeekend(toDate(day))) out.push({ day, title });
  }
  return out.sort((x, y) => x.day.localeCompare(y.day));
}

/** Today in the browser's (the warehouse's) zone. */
export function localToday(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** `10/15/26` — the grid's date format. */
export const formatDay = (day: string) => `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(2, 4)}`;
/** `10/15` */
export const formatShortDay = (day: string) => `${day.slice(5, 7)}/${day.slice(8, 10)}`;
/** `Thu` */
export const dayOfWeek = (day: string) => DOW[toDate(day).getUTCDay()] ?? "";

/**
 * "This week" and "next week" as people ask them: the calendar week, Monday to Sunday — this week
 * from today. Through Sunday, not Friday: a FIRST SHIP or CANCEL can fall on a weekend, and a week
 * ending Friday would leave those lines out (a Route by is always a business day either way).
 */
export function weekRange(today: string, which: "this" | "next"): { from: string; to: string } {
  const monday = weekStart(today);
  return which === "this"
    ? { from: today, to: shiftDay(monday, 6) }
    : { from: shiftDay(monday, 7), to: shiftDay(monday, 13) };
}
